import React, { useState, useEffect } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Label,
  Input,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import ActionButton from '../../../Components/Common/ActionButton';
import DeleteModal from '../../../Components/Common/DeleteModal';
import Can from '../../../Components/Common/Can';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { proHydroApi } from '../../../helpers/crude_api_helper';

const FUNGSI_DELIMITER = ';';

const emptyForm = {
  id: null,
  jenisPengadaanId: '',
  nomor: '',
  aktifitasLelangName: '',
  fungsiCodes: [],
  isActive: true,
};

const MatrixEvaluasiTenderView = () => {
  const [items, setItems] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [fungsiLoading, setFungsiLoading] = useState(false);

  const [jenisPengadaanOptions, setJenisPengadaanOptions] = useState([]);
  const [fungsiOptions, setFungsiOptions] = useState([]);

  const [modalForm, setModalForm] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  // Pagination exception (per rule doc's literal "Default Load: 10 Data",
  // confirmed for this page) — repo-wide crud pages otherwise default to 15.
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  document.title = 'Matrix Evaluasi Tender';

  useEffect(() => {
    fetchJenisPengadaan();
    fetchFungsi();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, rowsPerPage, debouncedSearch, sortField, sortDirection]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, sortField, sortDirection]);

  const fetchData = async () => {
    setDataLoading(true);
    try {
      const params = { page: currentPage, pageSize: rowsPerPage };
      if (debouncedSearch) params.searchText = debouncedSearch;
      if (sortField) {
        params.sortField = sortField;
        params.sortOrder = sortDirection === 'asc' ? 'Asc' : 'Desc';
      }
      const result = await proHydroApi.get('/v1/AktifitasLelangs', params);
      const list = Array.isArray(result?.items) ? result.items : [];
      setItems(list);
      setTotalRows(result?.totalCount || 0);
    } catch (error) {
      toast.error(`Failed to load data: ${error}`);
      setItems([]);
      setTotalRows(0);
    } finally {
      setDataLoading(false);
    }
  };

  const fetchJenisPengadaan = async () => {
    try {
      const result = await proHydroApi.get('/v1/JenisPengadaans/Lookup');
      const list = Array.isArray(result?.items) ? result.items : [];
      setJenisPengadaanOptions(list);
    } catch (error) {
      toast.error(`Failed to load Jenis Pengadaan: ${error}`);
      setJenisPengadaanOptions([]);
    }
  };

  const fetchFungsi = async () => {
    setFungsiLoading(true);
    try {
      // GET /v1/Fungsis is itself paginated (default pageSize 10) — request
      // the contract's max pageSize so every Fungsi renders as a matrix
      // column instead of silently truncating to the first page.
      const result = await proHydroApi.get('/v1/Fungsis', {
        page: 1,
        pageSize: 100,
      });
      const list = Array.isArray(result?.items) ? result.items : [];
      setFungsiOptions(list);
    } catch (error) {
      toast.error(`Failed to load Fungsi: ${error}`);
      setFungsiOptions([]);
    } finally {
      setFungsiLoading(false);
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const handleStatusChange = e => {
    setFormData({ ...formData, isActive: e.target.checked });
  };

  const handleFungsiToggle = fungsiCode => {
    setFormData(prev => {
      const exists = prev.fungsiCodes.includes(fungsiCode);
      return {
        ...prev,
        fungsiCodes: exists
          ? prev.fungsiCodes.filter(code => code !== fungsiCode)
          : [...prev.fungsiCodes, fungsiCode],
      };
    });
    setErrors(prev => ({ ...prev, parameterOtorisasiFungsi: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.jenisPengadaanId) {
      newErrors.jenisPengadaanId = 'Jenis Pengadaan is required';
    }
    if (!formData.nomor || !formData.nomor.trim()) {
      newErrors.nomor = 'Nomor is required';
    } else if (formData.nomor.trim().length > 5) {
      newErrors.nomor = 'Nomor max 5 characters';
    }
    if (!formData.aktifitasLelangName || !formData.aktifitasLelangName.trim()) {
      newErrors.aktifitasLelangName = 'Proses Tender is required';
    } else if (formData.aktifitasLelangName.trim().length > 100) {
      newErrors.aktifitasLelangName = 'Proses Tender max 100 characters';
    }
    if (formData.fungsiCodes.join(FUNGSI_DELIMITER).length > 50) {
      newErrors.parameterOtorisasiFungsi =
        'Too many Fungsi selected (max 50 characters combined)';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNewModal = () => {
    setFormData(emptyForm);
    setErrors({});
    setModalForm(true);
  };

  const handleUpdateModal = row => {
    setFormData({
      id: row.id,
      jenisPengadaanId: row.jenisPengadaanId || '',
      nomor: row.nomor || '',
      aktifitasLelangName: row.aktifitasLelangName || '',
      fungsiCodes: (row.parameterOtorisasiFungsi || '')
        .split(FUNGSI_DELIMITER)
        .map(code => code.trim())
        .filter(Boolean),
      isActive: !!row.isActive,
    });
    setErrors({});
    setModalForm(true);
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const basePayload = {
      jenisPengadaanId: formData.jenisPengadaanId,
      nomor: formData.nomor.trim(),
      aktifitasLelangName: formData.aktifitasLelangName.trim(),
      parameterOtorisasiFungsi: formData.fungsiCodes.length
        ? formData.fungsiCodes.join(FUNGSI_DELIMITER)
        : null,
    };

    if (formData.id) {
      try {
        await proHydroApi.put(`/v1/AktifitasLelangs/${formData.id}`, {
          id: formData.id,
          ...basePayload,
          isActive: formData.isActive,
          aktifitasLelangs: [],
        });
        toast.success('Matrix Evaluasi Tender has been updated');
      } catch (error) {
        toast.error(`Failed to update data: ${error}`);
      } finally {
        setModalForm(false);
        fetchData();
      }
    } else {
      try {
        // isActive intentionally omitted — server sets it true automatically.
        await proHydroApi.create('/v1/AktifitasLelangs', basePayload);
        toast.success('Matrix Evaluasi Tender has been added');
      } catch (error) {
        toast.error(`Failed to save data: ${error}`);
      } finally {
        setModalForm(false);
        fetchData();
      }
    }
  };

  const handleDeleteModal = row => {
    setSelectedRow(row);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    try {
      await proHydroApi.delete(`/v1/AktifitasLelangs/${selectedRow.id}`);
      toast.success('Matrix Evaluasi Tender has been deleted');
    } catch (error) {
      toast.error(`Failed to delete data: ${error}`);
    } finally {
      setDeleteModal(false);
      setSelectedRow(null);
      fetchData();
    }
  };

  // Reorder is scoped to the currently-loaded page — the list is now
  // server-paginated and server-sorted by SortOrder, so there is no
  // cross-page neighbor to swap with.
  const moveRow = async (row, direction) => {
    const index = items.findIndex(item => item.id === row.id);
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= items.length) return;

    const current = items[index];
    const swapWith = items[swapIndex];

    try {
      await proHydroApi.put('/v1/AktifitasLelangs/sort-order', {
        aktifitasLelangs: [
          { id: current.id, sortOrder: swapWith.sortOrder },
          { id: swapWith.id, sortOrder: current.sortOrder },
        ],
      });
      fetchData();
    } catch (error) {
      toast.error(`Failed to reorder data: ${error}`);
    }
  };

  // Gate the table's loading indicator on BOTH fetches so rows and the full
  // set of Fungsi columns always appear together in one paint, instead of
  // rows showing first and Fungsi columns popping in a moment later.
  const loading = dataLoading || fungsiLoading;

  const headerLabel = label => (
    <span className='form-label text-uppercase fw-semibold fs-13 mb-0'>
      {label}
    </span>
  );

  const toPascalCase = field =>
    field ? field.charAt(0).toUpperCase() + field.slice(1) : field;

  const handleSort = (column, direction) => {
    setSortField(column.id ? toPascalCase(column.id) : null);
    setSortDirection(direction);
  };

  const leadingColumns = [
    {
      id: 'jenisPengadaanName',
      name: headerLabel('Jenis Pengadaan'),
      // Server doesn't support sorting/searching on the joined
      // jenisPengadaanCode/Name columns — see api-contract "Catatan FE".
      sortable: false,
      selector: row =>
        row.jenisPengadaanCode || row.jenisPengadaanName
          ? `${row.jenisPengadaanCode || ''} - ${row.jenisPengadaanName || ''}`
          : '',
      wrap: true,
      width: '220px',
    },
    {
      id: 'nomor',
      name: headerLabel('Nomor'),
      selector: row => row.nomor,
      sortable: true,
      wrap: true,
      width: '110px',
    },
    {
      id: 'aktifitasLelangName',
      name: headerLabel('Proses Tender'),
      selector: row => row.aktifitasLelangName,
      sortable: true,
      wrap: true,
      width: '260px',
    },
  ];

  const trailingColumns = [
    {
      id: 'isActive',
      name: headerLabel('Status'),
      sortable: true,
      width: '110px',
      center: true,
      cell: row => (
        <div className='form-check d-flex justify-content-center'>
          <Input
            type='checkbox'
            className='form-check-input'
            checked={!!row.isActive}
            readOnly
            disabled
          />
        </div>
      ),
    },
    {
      name: headerLabel('Order'),
      width: '110px',
      center: true,
      cell: row => {
        const index = items.findIndex(item => item.id === row.id);
        return (
          <div className='d-flex gap-1'>
            <ActionButton
              variant='icon'
              size='sm'
              icon='ri-arrow-up-line'
              label='Move up'
              disabled={index <= 0}
              onClick={() => moveRow(row, 'up')}
            />
            <ActionButton
              variant='icon'
              size='sm'
              icon='ri-arrow-down-line'
              label='Move down'
              disabled={index === items.length - 1}
              onClick={() => moveRow(row, 'down')}
            />
          </div>
        );
      },
    },
    {
      name: headerLabel('Action'),
      width: '110px',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-end'>
          <Can action='canUpdate'>
            <ActionButton
              variant='icon'
              size='sm'
              icon='ri-edit-line'
              label='Edit'
              onClick={() => handleUpdateModal(row)}
            />
          </Can>
          <Can action='canDelete'>
            <ActionButton
              variant='icon'
              size='sm'
              danger
              icon='ri-delete-bin-line'
              label='Delete'
              confirm={false}
              onClick={() => handleDeleteModal(row)}
            />
          </Can>
        </div>
      ),
    },
  ];

  // One dynamically-generated column per Fungsi, per list.png reference
  // (auto generated from master Fungsi, not a single combined column).
  const fungsiColumns = fungsiOptions.map(f => ({
    name: headerLabel(f.fungsiCode),
    width: '90px',
    center: true,
    cell: row => {
      const codes = (row.parameterOtorisasiFungsi || '')
        .split(FUNGSI_DELIMITER)
        .map(code => code.trim());
      return (
        <div className='form-check d-flex justify-content-center'>
          <Input
            type='checkbox'
            className='form-check-input'
            checked={codes.includes(f.fungsiCode)}
            readOnly
            disabled
          />
        </div>
      );
    },
  }));

  const columns = [...leadingColumns, ...fungsiColumns, ...trailingColumns];

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDelete}
          onCloseClick={() => setDeleteModal(false)}
        />
        <Container fluid>
          <BreadCrumb title='Matrix Evaluasi Tender' pageTitle='CRUDE' />

          <Row>
            <Col lg={12}>
              <Card id='MatrixEvaluasiTenderList'>
                <CardHeader className='border-0'>
                  <div className='d-flex align-items-center flex-wrap gap-2'>
                    <h5 className='card-title mb-0 flex-grow-1'>
                      Matrix Evaluasi Tender
                    </h5>
                    <div className='flex-shrink-0'>
                      <div className='d-flex gap-2 flex-wrap'>
                        <div className='search-box'>
                          <Input
                            type='text'
                            className='form-control form-control-sm'
                            placeholder='Search...'
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                          />
                        </div>
                        <Can action='canCreate'>
                          <ActionButton
                            variant='primary'
                            size='sm'
                            icon='ri-play-list-add-line'
                            onClick={handleNewModal}
                          >
                            <span className='d-none d-sm-inline'>
                              Add Matrix Evaluasi Tender
                            </span>
                            <span className='d-sm-none'>New</span>
                          </ActionButton>
                        </Can>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardBody className='pt-0'>
                  <div
                    className='table-responsive'
                    style={{
                      minHeight: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <DataTable
                      columns={columns}
                      highlightOnHover
                      data={items}
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      paginationPerPage={rowsPerPage}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      onChangePage={page => setCurrentPage(page)}
                      onChangeRowsPerPage={newPerPage => {
                        setRowsPerPage(newPerPage);
                        setCurrentPage(1);
                      }}
                      sortServer
                      onSort={handleSort}
                      wrap
                      dense
                      striped
                      progressPending={loading}
                      fixedHeader={false}
                      noDataComponent={
                        <div
                          style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6c757d',
                          }}
                        >
                          <i
                            className='ri-database-2-line'
                            style={{
                              fontSize: '32px',
                              marginBottom: '8px',
                              display: 'block',
                            }}
                          ></i>
                          No matrix evaluasi tender found
                        </div>
                      }
                      customStyles={{
                        header: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            fontWeight: 'bold',
                          },
                        },
                        headCells: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            borderRight: '1px solid #dee2e6',
                            position: 'relative',
                            overflow: 'visible',
                          },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                            borderRight: '1px solid #dee2e6',
                          },
                        },
                        rows: {
                          style: {
                            minHeight: '50px',
                            '&:hover': {
                              backgroundColor: '#f8f9fa',
                            },
                          },
                        },
                        table: {
                          style: {
                            width: '100%',
                            minWidth: '100%',
                          },
                        },
                      }}
                    />

                    <ToastContainer closeButton={false} limit={1} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal
            isOpen={modalForm}
            role='dialog'
            autoFocus={true}
            centered
            id='addMatrixEvaluasiTenderModal'
            toggle={() => setModalForm(!modalForm)}
            data-bs-backdrop='static'
            size='md'
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => setModalForm(!modalForm)}
            >
              <span
                className='modal-title text-info fw-bold text-truncate d-inline-block'
                style={{ maxWidth: '380px' }}
              >
                {formData.id !== null
                  ? `Details of ${formData.id}`
                  : 'Add New Record'}
              </span>
            </ModalHeader>

            <ModalBody>
              <div className='d-flex mt-2'>
                <Col sm={12}>
                  <Row>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='jenisPengadaanId' className='form-label'>
                          Jenis Pengadaan <span className='text-danger'>*</span>
                        </Label>
                        <Input
                          type='select'
                          id='jenisPengadaanId'
                          name='jenisPengadaanId'
                          className='form-control'
                          invalid={!!errors.jenisPengadaanId}
                          value={formData.jenisPengadaanId || ''}
                          onChange={handleChange}
                        >
                          <option value=''>Select Jenis Pengadaan</option>
                          {jenisPengadaanOptions.map(opt => (
                            <option key={opt.id} value={opt.id}>
                              {opt.text}
                            </option>
                          ))}
                        </Input>
                        {errors.jenisPengadaanId && (
                          <div className='invalid-feedback d-block'>
                            {errors.jenisPengadaanId}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='nomor' className='form-label'>
                          Nomor <span className='text-danger'>*</span>
                        </Label>
                        <Input
                          type='text'
                          id='nomor'
                          name='nomor'
                          className='form-control'
                          maxLength={5}
                          invalid={!!errors.nomor}
                          value={formData.nomor || ''}
                          onChange={handleChange}
                        />
                        {errors.nomor && (
                          <div className='invalid-feedback d-block'>
                            {errors.nomor}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='aktifitasLelangName' className='form-label'>
                          Proses Tender <span className='text-danger'>*</span>
                        </Label>
                        <Input
                          type='text'
                          id='aktifitasLelangName'
                          name='aktifitasLelangName'
                          className='form-control'
                          maxLength={100}
                          invalid={!!errors.aktifitasLelangName}
                          value={formData.aktifitasLelangName || ''}
                          onChange={handleChange}
                        />
                        {errors.aktifitasLelangName && (
                          <div className='invalid-feedback d-block'>
                            {errors.aktifitasLelangName}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label className='form-label'>Fungsi</Label>
                        <div
                          className='border rounded p-2'
                          style={{ maxHeight: '180px', overflowY: 'auto' }}
                        >
                          {fungsiOptions.map(f => (
                            <div className='form-check' key={f.id}>
                              <Input
                                type='checkbox'
                                className='form-check-input'
                                id={`fungsi-${f.id}`}
                                checked={formData.fungsiCodes.includes(
                                  f.fungsiCode
                                )}
                                onChange={() =>
                                  handleFungsiToggle(f.fungsiCode)
                                }
                              />
                              <Label
                                for={`fungsi-${f.id}`}
                                className='form-check-label'
                              >
                                {f.fungsiCode} ({f.fungsiName})
                              </Label>
                            </div>
                          ))}
                          {fungsiOptions.length === 0 && (
                            <span className='text-muted fs-13'>
                              No Fungsi data available
                            </span>
                          )}
                        </div>
                        {errors.parameterOtorisasiFungsi && (
                          <div className='invalid-feedback d-block'>
                            {errors.parameterOtorisasiFungsi}
                          </div>
                        )}
                      </div>
                    </Col>
                    {formData.id !== null && (
                      <Col sm={12}>
                        <div className='mb-3'>
                          <Label className='form-label'>Status</Label>
                          <div className='form-check'>
                            <Input
                              type='checkbox'
                              id='isActive'
                              name='isActive'
                              className='form-check-input'
                              checked={!!formData.isActive}
                              onChange={handleStatusChange}
                            />
                            <Label for='isActive' className='form-check-label'>
                              Active
                            </Label>
                          </div>
                        </div>
                      </Col>
                    )}
                  </Row>
                </Col>
              </div>
            </ModalBody>

            <ModalFooter>
              <ActionButton
                variant='secondary'
                size='sm'
                icon='ri-close-line'
                onClick={() => setModalForm(false)}
              >
                Cancel
              </ActionButton>
              <ActionButton
                variant='primary'
                size='sm'
                icon='ri-save-line'
                onClick={handleSave}
              >
                Save
              </ActionButton>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default MatrixEvaluasiTenderView;
